'use client';

import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { integrationsApi, Integration, IntegrationConnection } from '@/api/integrations';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from './AuthContext';

interface IntegrationContextType {
  integrations: {
    data: Integration[] | null;
    isLoading: boolean;
    error: Error | null;
  };
  connections: {
    data: IntegrationConnection[] | null;
    isLoading: boolean;
    error: Error | null;
  };
  fetchIntegrations: () => Promise<void>;
  fetchConnections: () => Promise<void>;
  connectZoho: () => Promise<void>;
  verifyConnection: (connectionId: string) => Promise<boolean>;
  disconnectIntegration: (connectionId: string) => Promise<void>;
}

const IntegrationContext = createContext<IntegrationContextType | undefined>(undefined);

export function IntegrationProvider({ children }: { children: React.ReactNode }) {
  const { toast } = useToast();
  const { isAuthenticated, isInitialized } = useAuth();
  const [integrations, setIntegrations] = useState<{
    data: Integration[] | null;
    isLoading: boolean;
    error: Error | null;
  }>({
    data: null,
    isLoading: false,
    error: null,
  });

  const [connections, setConnections] = useState<{
    data: IntegrationConnection[] | null;
    isLoading: boolean;
    error: Error | null;
  }>({
    data: null,
    isLoading: false,
    error: null,
  });

  const fetchIntegrations = useCallback(async () => {
    // Only fetch if user is authenticated
    if (!isAuthenticated) {
      setIntegrations({ data: null, isLoading: false, error: null });
      return;
    }

    setIntegrations((prev) => ({ ...prev, isLoading: true, error: null }));
    try {
      const data = await integrationsApi.getIntegrations();
      setIntegrations({ data, isLoading: false, error: null });
    } catch (error) {
      const err = error instanceof Error ? error : new Error('Failed to fetch integrations');
      // Don't show toast for 401 errors (expected when not authenticated)
      const isAuthError = err.message.includes('401') || err.name === 'HTTP401';
      setIntegrations({ data: null, isLoading: false, error: err });
      if (!isAuthError) {
        toast({
          description: err.message,
          variant: 'destructive',
        });
      }
    }
  }, [toast, isAuthenticated]);

  const fetchConnections = useCallback(async () => {
    // Only fetch if user is authenticated
    if (!isAuthenticated) {
      setConnections({ data: null, isLoading: false, error: null });
      return;
    }

    setConnections((prev) => ({ ...prev, isLoading: true, error: null }));
    try {
      const data = await integrationsApi.getConnections();
      setConnections({ data, isLoading: false, error: null });
    } catch (error) {
      const err = error instanceof Error ? error : new Error('Failed to fetch connections');
      // Don't show toast for 401 errors (expected when not authenticated)
      const isAuthError = err.message.includes('401') || err.name === 'HTTP401';
      setConnections({ data: null, isLoading: false, error: err });
      if (!isAuthError) {
        toast({
          description: err.message,
          variant: 'destructive',
        });
      }
    }
  }, [toast, isAuthenticated]);

  const connectZoho = useCallback(async () => {
    try {
      const authUrl = await integrationsApi.connectZoho();
      // Redirect to Zoho OAuth
      window.location.href = authUrl;
    } catch (error) {
      const err = error instanceof Error ? error : new Error('Failed to initiate Zoho connection');
      toast({
        description: err.message,
        variant: 'destructive',
      });
      throw err;
    }
  }, [toast]);

  const verifyConnection = useCallback(
    async (connectionId: string): Promise<boolean> => {
      try {
        const result = await integrationsApi.verifyConnection(connectionId);
        toast({
          description: result.isValid
            ? 'Connection verified successfully'
            : 'Connection verification failed',
          variant: result.isValid ? 'default' : 'destructive',
        });
        // Refresh connections after verification
        await fetchConnections();
        return result.isValid;
      } catch (error) {
        const err = error instanceof Error ? error : new Error('Failed to verify connection');
        toast({
          description: err.message,
          variant: 'destructive',
        });
        return false;
      }
    },
    [toast, fetchConnections]
  );

  const disconnectIntegration = useCallback(
    async (connectionId: string) => {
      try {
        await integrationsApi.disconnectIntegration(connectionId);
        toast({
          description: 'Integration disconnected successfully',
        });
        // Refresh connections after disconnecting
        await fetchConnections();
      } catch (error) {
        const err = error instanceof Error ? error : new Error('Failed to disconnect integration');
        toast({
          description: err.message,
          variant: 'destructive',
        });
        throw err;
      }
    },
    [toast, fetchConnections]
  );

  // Fetch integrations on mount, but only if user is authenticated
  useEffect(() => {
    // Wait for auth to initialize before attempting to fetch
    if (!isInitialized) {
      return;
    }

    if (isAuthenticated) {
      fetchIntegrations();
      fetchConnections();
    }
  }, [isAuthenticated, isInitialized, fetchIntegrations, fetchConnections]);

  return (
    <IntegrationContext.Provider
      value={{
        integrations,
        connections,
        fetchIntegrations,
        fetchConnections,
        connectZoho,
        verifyConnection,
        disconnectIntegration,
      }}
    >
      {children}
    </IntegrationContext.Provider>
  );
}

export function useIntegration() {
  const context = useContext(IntegrationContext);
  if (context === undefined) {
    throw new Error('useIntegration must be used within an IntegrationProvider');
  }
  return context;
}





